import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { firstPasswordChangeStart } from "../../store/features/firstPasswordChange/firstPasswordChangeSlice";
import { useNavigate } from "react-router";
import { validatePassword } from "./utils";
import CustomAlert from "../alert/alert.component";

const InitialPasswordChangeForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [alert, setAlert] = useState(false);
  const searchParams = new URLSearchParams(window.location.search);
  const uid = searchParams.get("uid");
  const { isLoading, error, data } = useSelector(
    (state) => state.firstPasswordChange
  );
  const access_token = data?.access_token;

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      old_password: "",
      new_password: "",
      confirm_password: "",
    },
    mode: "onChange",
  });

  const newPassword = watch("new_password");

  const onSubmit = (data) => {
    if (!isLoading) {
      dispatch(
        firstPasswordChangeStart({
          old_password: data.old_password,
          new_password: data.new_password,
          uid,
        })
      );
    }
  };

  useEffect(() => {
    if (access_token) {
      setAlert({
        severity: "success",
        message:
          "Password modificata con successo! Verrai reindirizzato al login.",
      });
      const timer = setTimeout(() => {
        navigate("/login");
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [navigate, access_token]);

  return (
    <Box width="100%">
      {alert && (
        <CustomAlert severity={alert.severity} message={alert.message} />
      )}
      <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
        <Controller
          name="old_password"
          control={control}
          rules={{
            required: "La password è obbligatoria",
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              variant="filled"
              label="Vecchia Password"
              placeholder="Vecchia Password"
              autoComplete="current-password"
              type={showOldPassword ? "text" : "password"}
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowOldPassword(!showOldPassword)}
                      edge="end"
                      disabled={isLoading}
                    >
                      {showOldPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              error={!!errors.old_password || !!error}
              helperText={
                errors.old_password?.message ||
                (error && "Credenziali non valide")
              }
              disabled={isLoading}
            />
          )}
        />
        <Controller
          name="new_password"
          control={control}
          rules={{
            required: "La password è obbligatoria",
            validate: (value) => validatePassword(value),
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              variant="filled"
              label="Nuova Password"
              placeholder="Nuova Password"
              autoComplete="new-password"
              sx={{ mb: 2 }}
              type={showNewPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                      disabled={isLoading}
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              error={!!errors.new_password || !!error}
              helperText={
                errors.new_password?.message ||
                (error && "Credenziali non valide")
              }
              disabled={isLoading}
            />
          )}
        />
        <Controller
          name="confirm_password"
          control={control}
          rules={{
            required: "La conferma password è obbligatoria",
            validate: (value) =>
              value === newPassword || "Le password non corrispondono",
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              variant="filled"
              label="Conferma Password"
              placeholder="Nuova Password"
              autoComplete="confirm-password"
              sx={{ mb: 2 }}
              type={showConfirmPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      edge="end"
                      disabled={isLoading}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              error={!!errors.confirm_password}
              helperText={errors.confirm_password?.message}
              disabled={isLoading}
            />
          )}
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          sx={{ mt: 2, textTransform: "none", height: "50px" }}
          disabled={isLoading || !!alert}
        >
          Cambia Password
        </Button>
      </form>
    </Box>
  );
};

export default InitialPasswordChangeForm;
